import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { LANGS, DEFAULT_LANG, HTML_LANG, OG_LOCALE, getLangFromPath, localizePath, stripLang } from '../i18n/langs';
import { SEO_META, BRAND, HOME_LABEL } from '../i18n/seoMeta';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  type?: 'website' | 'article';
  schema?: Record<string, any>;
  noindex?: boolean;
  datePublished?: string;
}

export const SEO: React.FC<SEOProps> = ({
  title: rawTitle,
  description: rawDescription,
  noindex,
  datePublished,
  canonical,
  image,
  type = 'website',
  schema
}) => {
  // Dominio canónico: con www y con barra final (así responde el servidor, sin redirecciones)
  const siteUrl = 'https://www.electricista-urgencias-24-horas-barcelona.com';
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  // Ruta sin prefijo de idioma (p. ej. "/blog/mi-articulo")
  const basePath = stripLang(canonical || location.pathname);
  const urlFor = (lng: string) => {
    const p = localizePath(basePath, lng);
    return p === '/' ? `${siteUrl}/` : `${siteUrl}${p}/`;
  };
  const fullUrl = urlFor(lang);
  const abs = (p: string) => (p === '/' ? siteUrl + '/' : siteUrl + p + '/');
  // Títulos/descripciones SEO por idioma (src/i18n/seoMeta.ts); si no hay, se usan los de la página
  const meta = SEO_META[basePath]?.[lang];
  const title = meta?.title ?? rawTitle;
  const description = meta?.description ?? rawDescription;
  const isNoindex = Boolean(noindex || meta?.noindex);
  const robots = isNoindex ? 'noindex, follow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
  const isPost = basePath.startsWith('/blog/');
  const breadcrumbSchema = basePath === '/' ? null : {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": HOME_LABEL[lang], "item": abs(localizePath('/', lang)) },
      ...(isPost ? [{ "@type": "ListItem", "position": 2, "name": "Blog", "item": abs(localizePath('/blog', lang)) }] : []),
      { "@type": "ListItem", "position": isPost ? 3 : 2, "name": title.split(' | ')[0], "item": fullUrl }
    ]
  };
  // Imagen propia para compartir (1200x630) y logo (512x512), en /public
  const defaultImage = siteUrl + '/og-image.jpg';
  const logoUrl = siteUrl + '/logo.png';
  const finalImage = image || defaultImage;

  const defaultSchema = {
    "@context": "https://schema.org",
    "@type": "Electrician",
    "name": BRAND,
    "alternateName": "Electricista Lampista Urgencias 24h",
    "areaServed": [
      { "@type": "City", "name": "Barcelona" },
      { "@type": "AdministrativeArea", "name": "Barcelonès" },
      { "@type": "AdministrativeArea", "name": "Baix Llobregat" },
      { "@type": "AdministrativeArea", "name": "Garraf" }
    ],
    "availableLanguage": ["es", "ca", "en", "fr"],
    "image": defaultImage,
    "logo": logoUrl,
    "description": SEO_META['/'][lang].description,
    "@id": siteUrl + '/#business',
    "url": siteUrl + '/',
    "telephone": "+34600238283",
    "email": "info@electricista-urgencias-24-horas-barcelona.com",
    "priceRange": "€€",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Carrer de Trinxant, 128, local derecho",
      "addressLocality": "Barcelona",
      "postalCode": "08041",
      "addressRegion": "Cataluña",
      "addressCountry": "ES"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 41.4175,
      "longitude": 2.1819
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday"
      ],
      "opens": "00:00",
      "closes": "23:59"
    },
    "hasMap": "https://maps.app.goo.gl/rQw99Xgv2xom5nvM7",
    "sameAs": [
      "https://maps.app.goo.gl/rQw99Xgv2xom5nvM7"
    ]
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Electrical Services",
    "provider": { "@type": "Electrician", "@id": siteUrl + '/#business', "name": BRAND },
    "areaServed": {
      "@type": "City",
      "name": "Barcelona"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": siteUrl + '/',
      "servicePhone": "+34600238283"
    }
  };

  return (
    <Helmet htmlAttributes={{ lang: HTML_LANG[lang] }}>
      {/* Basic */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={finalImage} />
      {!image && <meta property="og:image:width" content="1200" />}
      {!image && <meta property="og:image:height" content="630" />}
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="Electricista Urgencias 24h Barcelona" />
      <meta property="og:locale" content={OG_LOCALE[lang]} />
      {LANGS.filter((l) => l !== lang).map((l) => (
        <meta key={`og-${l}`} property="og:locale:alternate" content={OG_LOCALE[l]} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={finalImage} />

      {/* Additional SEO */}
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="theme-color" content="#EAB308" />
      <meta name="geo.region" content="ES-CT" />
      <meta name="geo.placename" content="Barcelona" />
      <meta name="geo.position" content="41.4175;2.1819" />
      <meta name="ICBM" content="41.4175, 2.1819" />

      {/* Language Alternates */}
      {LANGS.map((l) => (
        <link key={`alt-${l}`} rel="alternate" hrefLang={HTML_LANG[l]} href={urlFor(l)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={urlFor(DEFAULT_LANG)} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(schema || (type === 'article' ? {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": title,
          "description": description,
          "image": finalImage,
          "author": {
            "@type": "Organization",
            "name": "Electricista Urgencias 24h Barcelona"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Electricista Urgencias 24h Barcelona",
            "logo": {
          "@type": "ImageObject",
          "url": logoUrl
        }
          },
          "datePublished": datePublished || new Date().toISOString(),
          "dateModified": datePublished || new Date().toISOString(),
          "inLanguage": HTML_LANG[lang],
          "mainEntityOfPage": fullUrl
        } : defaultSchema))}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
      {breadcrumbSchema && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      )}
    </Helmet>
  );
};